import type { ConversaResumo } from '@/api/secretario';

/**
 * Como a tela fala de tempo, estado e contato — num lugar só, para o inbox e o
 * dossiê dizerem a mesma coisa da mesma forma.
 */

export type Tom = 'ok' | 'warn' | 'bad' | 'neutral';

const FUSO = 'America/Sao_Paulo';

function dataLocal(iso: string): { dia: string; hora: string; ano: string } {
  const d = new Date(iso);
  return {
    dia: d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: FUSO }),
    hora: d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: FUSO }),
    ano: d.toLocaleDateString('pt-BR', { year: 'numeric', timeZone: FUSO }),
  };
}

function diasDeDiferenca(iso: string, agora = new Date()): number {
  const fmt = (x: Date) => x.toLocaleDateString('en-CA', { timeZone: FUSO }); // AAAA-MM-DD no fuso
  const a = new Date(fmt(new Date(iso)));
  const b = new Date(fmt(agora));
  return Math.round((a.getTime() - b.getTime()) / 86_400_000);
}

/** "hoje às 10:03" / "ontem às 10:03" / "amanhã às 10:03" / "07/09 às 10:03" (com ano se for outro). */
export function quando(iso?: string | null, agora = new Date()): string {
  if (!iso) return '';
  const { dia, hora, ano } = dataLocal(iso);
  const dias = diasDeDiferenca(iso, agora);
  if (dias === 0) return `hoje às ${hora}`;
  if (dias === -1) return `ontem às ${hora}`;
  if (dias === 1) return `amanhã às ${hora}`;
  const anoAtual = agora.toLocaleDateString('pt-BR', { year: 'numeric', timeZone: FUSO });
  return `${dia}${ano !== anoAtual ? `/${ano}` : ''} às ${hora}`;
}

/** O prazo-limite, sem hora: "até hoje" / "até amanhã" / "até 12/09". */
export function ate(iso?: string | null, agora = new Date()): string {
  if (!iso) return '';
  const dias = diasDeDiferenca(iso, agora);
  if (dias === 0) return 'até hoje';
  if (dias === 1) return 'até amanhã';
  return `até ${dataLocal(iso).dia}`;
}

/** "há 3 dias" / "há 5 horas" / "há pouco". */
export function haQuanto(iso?: string | null, agora = new Date()): string {
  if (!iso) return '';
  const ms = agora.getTime() - new Date(iso).getTime();
  const horas = Math.floor(ms / 3_600_000);
  if (horas < 1) return 'há pouco';
  if (horas < 24) return `há ${horas} ${horas === 1 ? 'hora' : 'horas'}`;
  const dias = Math.floor(horas / 24);
  return `há ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}

export function linkWhatsApp(telefone: string): string {
  return `https://wa.me/${telefone.replace(/\D/g, '')}`;
}

/**
 * DDD + assinante, sem o 55, com o 9 do celular. O WhatsApp entrega celular
 * antigo SEM o 9 (+55 84 8862-1330); todo celular brasileiro tem o 9 desde 2016,
 * e o assinante de 8 dígitos que começa em 6-9 é celular. Fixo (2-5) fica com 8.
 * Mesma regra de `chave_telefone` no backend.
 */
export function chaveTelefone(telefone: string): string | null {
  let d = telefone.replace(/\D/g, '');
  if (d.startsWith('0')) d = d.slice(1);
  if (d.length >= 12) {
    if (!d.startsWith('55')) return null;
    d = d.slice(2);
  } else if (telefone.trim().startsWith('+')) {
    return null; // E.164 curto demais para ser +55: estrangeiro
  }
  if (d.length !== 10 && d.length !== 11) return null;
  if (d.length === 10 && '6789'.includes(d[2])) d = `${d.slice(0, 2)}9${d.slice(2)}`;
  if (d.length === 11 && d[2] !== '9') return null; // 9 dígitos sem o 9 não é celular brasileiro
  return d;
}

/** "(84) 98862-1330"; fixo "(84) 3222-1330"; fora do padrão brasileiro, como veio. */
export function formatarTelefone(telefone: string): string {
  const d = chaveTelefone(telefone);
  if (!d) return telefone;
  return d.length === 11 ? `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}` : `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
}

/** Ligação com o 9: discar o celular sem ele não completa. */
export function linkTelefone(telefone: string): string {
  const d = chaveTelefone(telefone);
  return d ? `tel:+55${d}` : `tel:${telefone.replace(/\s/g, '')}`;
}

/**
 * A busca por telefone acha o número como a pessoa o digitar: com ou sem o 9,
 * com ou sem o 55, com máscara. Compara os dígitos da busca com as formas do
 * número guardado.
 */
export function telefoneCasa(telefone: string, busca: string): boolean {
  const q = busca.replace(/\D/g, '');
  if (q === '') return false;
  const formas = [telefone.replace(/\D/g, '')];
  const chave = chaveTelefone(telefone);
  if (chave) {
    const semNove = chave.length === 11 ? `${chave.slice(0, 2)}${chave.slice(3)}` : chave;
    formas.push(chave, `55${chave}`, semNove, `55${semNove}`);
  }
  return formas.some((forma) => forma.includes(q));
}

export interface Situacao {
  rotulo: string;
  tom: Tom;
}

/** O estado como o advogado o lê — e com a cor do que precisa dele. */
export function situacaoDe(c: ConversaResumo): Situacao {
  if (c.estado === 'ESCALONAMENTO_HUMANO') {
    return c.assumido_em
      ? { rotulo: 'Escalado · assumido', tom: 'neutral' }
      : { rotulo: 'Escalado · não assumido', tom: 'bad' };
  }
  if (c.estado === 'CONCLUSAO') {
    if (c.desfecho === 'PROCESSO') return { rotulo: 'Virou processo', tom: 'ok' };
    if (c.desfecho === 'ENCERRADA') return { rotulo: 'Encerrado', tom: 'neutral' };
    if (!c.concluido_em) return { rotulo: 'Concluído · finalizando', tom: 'neutral' };
    return c.retorno_em
      ? { rotulo: 'Concluído · em preparação', tom: 'neutral' }
      : { rotulo: 'Concluído · aguarda retorno', tom: 'warn' };
  }
  if (c.estado === 'FORA_DE_ESCOPO') return { rotulo: 'Fora do escopo', tom: 'neutral' };
  if (c.estado === 'STATUS_PROCESSO') return { rotulo: 'Consulta de andamento', tom: 'neutral' };
  return { rotulo: `Em andamento · ${c.etapa ?? c.estado}`, tom: 'neutral' };
}

/** A frase do que precisa do advogado, na linha do inbox. */
export function fraseDoQuePrecisa(c: ConversaResumo, agora = new Date()): { texto: string; tom: Tom } {
  if (c.estado === 'ESCALONAMENTO_HUMANO') {
    if (c.assumido_em) {
      return { texto: `Assumido ${quando(c.assumido_em, agora)}${c.assumido_por_nome ? ` por ${c.assumido_por_nome}` : ''}`, tom: 'ok' };
    }
    // O rótulo categorizado, o mesmo do dossiê: o motivo cru pode ser o resumo inteiro da IA.
    const rotulo = c.escalonamento_rotulo?.trim();
    return { texto: rotulo ? `Escalado: ${rotulo.charAt(0).toLowerCase()}${rotulo.slice(1)}` : 'Escalado para você', tom: 'bad' };
  }
  if (c.estado === 'CONCLUSAO') {
    if (c.desfecho === 'PROCESSO') return { texto: `Vinculado a processo ${quando(c.desfecho_em, agora)}`, tom: 'ok' };
    if (c.desfecho === 'ENCERRADA') return { texto: `Encerrado ${quando(c.desfecho_em, agora)}`, tom: 'neutral' };
    if (!c.concluido_em) return { texto: 'Finalizando o atendimento', tom: 'neutral' };
    if (c.retorno_em) return { texto: `Retorno feito ${quando(c.retorno_em, agora)} · sem processo ${haQuanto(c.retorno_em, agora)}`, tom: 'neutral' };
    const vencido = c.prazo_ate ? new Date(c.prazo_ate).getTime() < agora.getTime() : false;
    return vencido
      ? { texto: `Retorno prometido ${ate(c.prazo_ate, agora)} · prazo vencido`, tom: 'bad' }
      : { texto: `Retorno prometido ao cliente ${ate(c.prazo_ate, agora)}`, tom: 'warn' };
  }
  const parada = agora.getTime() - new Date(c.ultima_atividade_em).getTime() > 48 * 3_600_000;
  return {
    texto: parada
      ? `O Secretário está cuidando · parada ${haQuanto(c.ultima_atividade_em, agora)}`
      : 'O Secretário está cuidando',
    tom: parada ? 'warn' : 'neutral',
  };
}

/** Classes do badge por tom. As variantes do shadcn não têm "aviso" nem "ok". */
export function classesDoTom(tom: Tom): string {
  switch (tom) {
    case 'ok':
      return 'border-emerald-600/30 bg-emerald-600/10 text-emerald-700 dark:text-emerald-400';
    case 'warn':
      return 'border-amber-600/30 bg-amber-500/15 text-amber-800 dark:text-amber-300';
    case 'bad':
      return 'border-destructive/30 bg-destructive/10 text-destructive';
    default:
      return 'border-border bg-muted text-muted-foreground';
  }
}

export function nomeExibido(c: { contato_nome?: string | null; contato_telefone: string }): string {
  return c.contato_nome?.trim() || formatarTelefone(c.contato_telefone);
}
